import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { createCustomer, checkDuplicateCustomer } from '../actions/api';
import { history } from '../routers/AppRouter';
import { StageToggles, ReferralStepper } from './DashboardPage';
import { STAGES, MAX_REFERRALS } from '../scoring';
import { apiErrorMessage } from './AuthLayout';

const blank = { name: '', contacted: false, qc: false, appset: false, appshown: false, sold: false, referrals: 0 };

// "Appointment set" for the furthest stage the customer has reached.
const furthestStage = (c) => {
  const reached = STAGES.filter((s) => c[s.key]);
  return reached.length ? reached[reached.length - 1].label : 'No stages yet';
};

const DuplicateNotice = ({ matches, confirming, onAddAnyway, onCancel }) => {
  const exact = matches.filter((m) => m.match === 'exact');
  const similar = matches.filter((m) => m.match === 'similar');
  return (
    <div className="duplicate-notice" role="status" aria-live="polite">
      {exact.length > 0 ? (
        <p className="duplicate-title">You already added this customer.</p>
      ) : (
        <p className="duplicate-title">Is this someone you already added? A customer of yours has a similar name.</p>
      )}
      <ul>
        {[...exact, ...similar].map(({ customer }) => (
          <li key={customer.id}>
            <b>{customer.name}</b>
            <span>{furthestStage(customer)}{customer.referrals ? `, ${customer.referrals} referrals` : ''}</span>
          </li>
        ))}
      </ul>
      {confirming ? (
        <div className="duplicate-actions">
          <Button variant="contained" onClick={onAddAnyway}>Add as a new customer</Button>
          <Button color="secondary" onClick={onCancel}>Don’t add</Button>
        </div>
      ) : (
        <p className="duplicate-hint">
          To update them instead, go to <Link to="/dashboard">My customers</Link>.
        </p>
      )}
    </div>
  );
};

const AddCustomer = () => {
  const [data, setData] = useState(blank);
  const [matches, setMatches] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [addAnother, setAddAnother] = useState(false);

  // Check for duplicates as they type, after a short pause.
  useEffect(() => {
    const name = data.name.trim();
    setConfirming(false);
    if (name.length < 2) {
      setMatches([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(() => {
      checkDuplicateCustomer(name).then((m) => alive && setMatches(m)).catch(() => {});
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [data.name]);

  const save = async (another, allowDuplicate) => {
    setBusy(true);
    setError('');
    try {
      await createCustomer({ ...data, name: data.name.trim() }, allowDuplicate);
      if (another) {
        setData(blank);
        setMatches([]);
        setConfirming(false);
        setBusy(false);
      } else {
        history.push('/dashboard');
      }
    } catch (e) {
      if (e.response && e.response.status === 409) {
        setMatches(e.response.data.error.matches || []);
        setConfirming(true);
      } else {
        setError(apiErrorMessage(e));
      }
      setBusy(false);
    }
  };

  // With a possible duplicate on screen, saving asks first.
  const submit = (another) => {
    setAddAnother(another);
    if (matches.length && !confirming) {
      setConfirming(true);
      return;
    }
    save(another, confirming);
  };

  const named = !!data.name.trim();

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Add customer</h1>
          <p>Mark any stages they’ve already reached. You can update them later from My customers.</p>
        </div>
      </div>
      <form className="panel form-panel" onSubmit={(e) => { e.preventDefault(); submit(false); }}>
        {error && <div className="form-error" role="alert">{error}</div>}
        <TextField label="Customer name" value={data.name} required autoFocus autoComplete="off"
          onChange={(e) => setData({ ...data, name: e.target.value })} sx={{ mb: matches.length ? 1.5 : 3 }} />
        {matches.length > 0 && (
          <DuplicateNotice
            matches={matches}
            confirming={confirming}
            onAddAnyway={() => save(addAnother, true)}
            onCancel={() => setConfirming(false)}
          />
        )}
        <fieldset>
          <legend>Stages reached</legend>
          <StageToggles customer={data} onToggle={(key) => setData({ ...data, [key]: !data[key] })} />
        </fieldset>
        <fieldset>
          <legend>Referrals</legend>
          <ReferralStepper value={data.referrals}
            onChange={(v) => setData({ ...data, referrals: Math.max(0, Math.min(MAX_REFERRALS, v)) })} />
        </fieldset>
        <div className="form-actions">
          <Button type="submit" variant="contained" size="large" disabled={busy || !named || confirming}>Save customer</Button>
          <Button size="large" variant="outlined" color="secondary" disabled={busy || !named || confirming}
            onClick={() => submit(true)}>Save and add another</Button>
          <Button size="large" color="secondary" onClick={() => history.push('/dashboard')}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default AddCustomer;
